import React, { useState, useRef, useEffect } from 'react';
import LaunchTree from './LaunchTree';
import LocationTree from './LocationTree';
import SpendingTree from './SpendingTree';

// Hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

// Mini preview component for the Spending Tree case study
const SpendingTreePreview = () => (
  <div className="cs-spending-preview">
    <div className="cs-spending-header">
      <div className="cs-spending-title">
        <svg width="11" height="18" viewBox="0 0 10 16" fill="#0057FF">
          <polygon points="5,0 10,8 5,16 0,8"/>
        </svg>
        <span>Should I Spend This Money?</span>
      </div>
      <div className="cs-spending-subtitle">BINARY GAME DECISION TREE // MARCH 2026</div>
    </div>
    
    <div className="cs-mini-tree">
      {/* START */}
      <div className="cs-mini-start">START</div>
      <div className="cs-mini-connector" />
      
      {/* Gate 01 */}
      <div className="cs-mini-gate-row">
        <div className="cs-mini-gate">
          <div className="cs-mini-gate-spine" />
          <div className="cs-mini-gate-body">
            <div className="cs-mini-gate-label">GATE 01 | YES/NO</div>
            <div className="cs-mini-gate-question">Is this groceries, gas, or rent?</div>
          </div>
        </div>
        <div className="cs-mini-exit-path">
          <div className="cs-mini-dash" />
          <span className="cs-mini-yes-label">YES</span>
          <div className="cs-mini-dash" />
          <div className="cs-mini-exit">Spend. Locked.</div>
        </div>
      </div>
      
      <div className="cs-mini-no-label">NO</div>
      <div className="cs-mini-connector" />
      
      {/* Gate 02 */}
      <div className="cs-mini-gate-row">
        <div className="cs-mini-gate">
          <div className="cs-mini-gate-spine" />
          <div className="cs-mini-gate-body">
            <div className="cs-mini-gate-label">GATE 02 | SELECT ZONE</div>
            <div className="cs-mini-gate-question">What zone am I in?</div>
          </div>
        </div>
      </div>
      
      {/* Zone chips */}
      <div className="cs-mini-zones">
        <span className="cs-zone-chip cs-zone-red">RED</span>
        <span className="cs-zone-chip cs-zone-yr">YEL-RED</span>
        <span className="cs-zone-chip cs-zone-yellow">YELLOW</span>
        <span className="cs-zone-chip cs-zone-gy">GRN-YEL</span>
        <span className="cs-zone-chip cs-zone-green">GREEN</span>
      </div>
      
      <div className="cs-mini-connector" />
      
      {/* Zone path hint */}
      <div className="cs-mini-gate-row">
        <div className="cs-mini-gate">
          <div className="cs-mini-gate-spine" />
          <div className="cs-mini-gate-body">
            <div className="cs-mini-gate-label">ZONE-SPECIFIC PATH</div>
            <div className="cs-mini-gate-question">4-5 gates per zone: credit cards, subscriptions, debts, outsourcing, discretionary</div>
          </div>
        </div>
      </div>
    </div>
    
    <div className="cs-mini-fade" />
  </div>
);

const CaseStudyCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [showLaunchTree, setShowLaunchTree] = useState(false);
  const [showLocationTree, setShowLocationTree] = useState(false);
  const [showSpendingTree, setShowSpendingTree] = useState(false);
  const containerRef = useRef(null);
  const isMobile = useIsMobile();
  
  const caseStudies = [
    {
      id: 1,
      title: "Where Should I Live?",
      meta: "VENTURE PARTNER / SAN FRANCISCO INVESTMENT FIRM",
      story: {
        before: "A venture partner from a San Francisco investment firm came in asking: ",
        surface: "should I live in San Francisco or Los Angeles?",
        middle: " After 30 minutes, the city question turned out to be something else entirely. The real question was: ",
        real: "am I willing to be seen at the scale my mission requires?",
        after: ""
      },
      result: "Clarity on the real question and a 6-gate identity tree anchored to mission and visibility. Once that was answered, the city picked itself. Stored in their dashboard.",
      artifact: {
        image: "https://customer-assets.emergentagent.com/job_landing-studio-3/artifacts/0os0fvxp_BinaryPreview.png",
        fullImage: "https://customer-assets.emergentagent.com/job_landing-studio-3/artifacts/gvymvhyj_EvanBinaryTree.png",
        interactive: 'location'
      }
    },
    {
      id: 2,
      title: "Should I Launch While Rebuilding?",
      meta: "FOUNDER / BINARY GAME / SELF-FACILITATED",
      story: {
        before: "The person behind Binary Game had to make a call: launch the product publicly while still getting back on his feet financially. The surface question was ",
        surface: "should I put this out there while my own life isn't fully together yet?",
        middle: " After running the tree, the real question was something deeper: ",
        real: "am I willing to show up and lead before everything looks right on paper?",
        after: ""
      },
      result: "Clarity that being at a financial low is uncomfortable but not a reason to stay invisible. A seven-step tree that separates \"I need money\" from \"this is the right move.\" The launch decision was made in under 30 minutes and stored in the dashboard.",
      artifact: {
        image: "https://customer-assets.emergentagent.com/job_decision-trees/artifacts/qhegjags_Screenshot%202026-02-27%20at%201.48.15%E2%80%AFPM.png",
        fullImage: "https://customer-assets.emergentagent.com/job_decision-trees/artifacts/qhegjags_Screenshot%202026-02-27%20at%201.48.15%E2%80%AFPM.png",
        interactive: 'launch'
      }
    },
    {
      id: 3,
      title: "Should I Spend This Money?",
      meta: "ENTREPRENEUR / BOOTSTRAPPED FOUNDER / MULTI-VENTURE",
      story: {
        before: "A bootstrapped founder running multiple ventures came in with a resource problem: ",
        surface: "how do I make spending decisions when cash is tight and every dollar matters?",
        middle: " After one session, the spending question turned out to be five separate systems. The real question was: ",
        real: "can I build rules that make the right call for me before survival brain takes over?",
        after: ""
      },
      result: "A complete zone-based spending system with five separate decision paths, one per financial state. Every purchase runs through the tree and gets a clear spend or hold. No thinking required on hard days. The rules do the thinking. Stored in their dashboard.",
      artifact: {
        image: null,
        fullImage: null,
        interactive: 'spending',
        isZoneBased: true
      }
    }
  ];

  const totalSlides = caseStudies.length;

  const goToSlide = (index) => {
    if (index >= 0 && index < totalSlides) {
      setCurrentIndex(index);
    }
  };

  const handlePrev = () => goToSlide(currentIndex - 1);
  const handleNext = () => goToSlide(currentIndex + 1);

  // Touch/drag handlers
  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartX(e.type === 'touchstart' ? e.touches[0].clientX : e.clientX);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const currentX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const diff = currentX - startX;
    setTranslateX(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    if (translateX > 100 && currentIndex > 0) {
      handlePrev();
    } else if (translateX < -100 && currentIndex < totalSlides - 1) {
      handleNext();
    }
    setTranslateX(0);
  };

  return (
    <div className="cs-carousel" data-testid="case-study-carousel">
      {/* Ghost Number */}
      <div className="cs-ghost-number">06</div>
      
      {/* Header */}
      <div className="cs-header">
        <span className="cs-label">CASE STUDIES</span>
        <h2 className="cs-heading">See the work.</h2>
        <p className="cs-subheading">Real sessions. Real questions. Real trees.</p>
      </div>

      {/* Carousel Container */}
      <div 
        className="cs-container"
        ref={containerRef}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        <div 
          className="cs-track"
          style={{
            transform: `translateX(calc(-${currentIndex * (isMobile ? 100 : 85)}% + ${translateX}px))`,
            transition: isDragging ? 'none' : 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {caseStudies.map((study, index) => (
            <div
              key={study.id}
              className={`cs-card-wrapper ${index === currentIndex ? 'active' : 'inactive'}`}
              style={{
                opacity: index === currentIndex ? 1 : 0.35,
                transform: index === currentIndex ? 'scale(1)' : 'scale(0.975)',
                cursor: index !== currentIndex ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (index !== currentIndex) {
                  goToSlide(index);
                }
              }}
            >
              {study.placeholder ? (
                <div className="cs-card cs-placeholder" data-testid="placeholder-card">
                  <div className="cs-placeholder-content">
                    <div className="cs-placeholder-icon">
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <rect x="0.5" y="0.5" width="31" height="31" stroke="#CCCCCC" strokeDasharray="4 4" fill="none"/>
                        <line x1="16" y1="8" x2="16" y2="24" stroke="#CCCCCC" strokeWidth="2"/>
                        <line x1="8" y1="16" x2="24" y2="16" stroke="#CCCCCC" strokeWidth="2"/>
                      </svg>
                    </div>
                    <span className="cs-placeholder-text">Next case study / coming soon</span>
                  </div>
                </div>
              ) : (
                <div className="cs-card" data-testid={`case-study-card-${study.id}`}>
                  {/* Session Title */}
                  <h3 className="cs-card-title">{study.title}</h3>
                  
                  {/* Meta */}
                  <p className="cs-card-meta">{study.meta}</p>
                  
                  {/* Divider */}
                  <hr className="cs-divider" />
                  
                  {/* Story */}
                  <p className="cs-card-story">
                    {study.story.before}
                    <strong>{study.story.surface}</strong>
                    {study.story.middle}
                    <strong>{study.story.real}</strong>
                    {study.story.after}
                  </p>
                  
                  {/* Result Block */}
                  <div className="cs-result">
                    <div className="cs-result-label">
                      <svg width="8" height="12" viewBox="0 0 8 12" fill="#0057FF">
                        <polygon points="4,0 8,6 4,12 0,6"/>
                      </svg>
                      <span>WHAT THEY LEFT WITH</span>
                    </div>
                    <p className="cs-result-text">{study.result}</p>
                  </div>
                  
                  {/* Artifact Box */}
                  <div className="cs-artifact">
                    <div className="cs-artifact-header">
                      {study.artifact.interactive === 'location' ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLocationTree(true);
                          }}
                          className="cs-artifact-link"
                        >
                          VIEW FULL TREE <span className="cs-arrow">↗</span>
                        </button>
                      ) : study.artifact.interactive === 'launch' ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowLaunchTree(true);
                          }}
                          className="cs-artifact-link"
                        >
                          VIEW FULL TREE <span className="cs-arrow">↗</span>
                        </button>
                      ) : study.artifact.interactive === 'spending' ? (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSpendingTree(true);
                          }}
                          className="cs-artifact-link"
                        >
                          VIEW FULL TREE <span className="cs-arrow">↗</span>
                        </button>
                      ) : (
                        <a 
                          href={study.artifact.fullImage} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="cs-artifact-link"
                        >
                          VIEW FULL TREE <span className="cs-arrow">↗</span>
                        </a>
                      )}
                    </div>
                    {study.artifact.isZoneBased ? (
                      <SpendingTreePreview />
                    ) : (
                      <div className="cs-artifact-image-container">
                        <img 
                          src={study.artifact.image} 
                          alt={study.title}
                          className="cs-artifact-image"
                        />
                        <div className="cs-artifact-fade" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Tree Modals */}
      {showLaunchTree && (
        <LaunchTree onClose={() => setShowLaunchTree(false)} />
      )}
      {showLocationTree && (
        <LocationTree onClose={() => setShowLocationTree(false)} />
      )}
      {showSpendingTree && (
        <SpendingTree onClose={() => setShowSpendingTree(false)} />
      )}

      {/* Navigation */}
      <div className="cs-nav">
        <div className="cs-nav-arrows">
          <button 
            className={`cs-nav-arrow ${currentIndex === 0 ? 'disabled' : ''}`}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            aria-label="Previous"
            data-testid="carousel-prev"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </button>
          <button 
            className={`cs-nav-arrow ${currentIndex === totalSlides - 1 ? 'disabled' : ''}`}
            onClick={handleNext}
            disabled={currentIndex === totalSlides - 1}
            aria-label="Next"
            data-testid="carousel-next"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </button>
        </div>
        
        <div className="cs-nav-dots">
          {caseStudies.map((_, index) => (
            <button
              key={index}
              className={`cs-nav-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="cs-nav-count">
          {currentIndex + 1} / {totalSlides}
        </div>
      </div>
    </div>
  );
};

export default CaseStudyCarousel;
